import {
  type CustomRow,
  type PortfolioBreakdownHierarchyNode,
  type PortfolioBreakdownRowData,
  type PortfolioRowData,
  type VennSeriesEntry,
} from 'venn-components';
import type { HoldingsCategoryInfo } from 'venn-state';
import { getColorOnIndex, getStrategyColorByPath, type Theme } from 'venn-ui-kit';
import { type CustomBlockTypeEnum, type CustomizableMetric, Numbers, useHasFF } from 'venn-utils';

import { getAssetExposurePieChartSeries } from './getAssetExposurePieChartSeries';
import { type ColorMap } from './portfolioBreakdown/fillColorMap';
import { getPortfolioBreakdownChartData } from './portfolioBreakdown/getPortfolioBreakdownChartData';

function privateGetPortfolioBreakdownRowData(
  data: PortfolioRowData<PortfolioBreakdownRowData>[],
  theme: Theme,
  metric?: Pick<CustomizableMetric, 'key' | 'label'>,
): VennSeriesEntry[] {
  return [
    {
      name: metric?.label,
      data: data?.flatMap((row, index) => {
        const name = row.key ?? '';
        const value = metric?.key === 'allocation' ? row.value[0]!.weight : undefined;
        const legend = `${name}: ${Numbers.safeFormatPercentage(value, 1)}`;
        return [
          {
            name,
            legend,
            y: value,
            color: getColorOnIndex(theme.Colors, index),
            fundId: row.fundId,
          },
        ];
      }),
    },
  ];
}

function privateGetCustomRCMPortfolioBreakdownRowData(
  data: PortfolioRowData<PortfolioBreakdownRowData>[],
  theme: Theme,
  metric?: Pick<CustomizableMetric, 'key' | 'label'>,
): VennSeriesEntry[] {
  return [
    {
      name: metric?.label,
      data: data?.flatMap((row, index) => {
        const name = row.key ?? '';
        const value = metric?.key === 'allocation' ? row.value[0]!.weight : undefined;
        const legend = `${name}: ${Numbers.safeFormatPercentage(value, 1)}`;

        return [
          {
            name,
            legend,
            y: value,
            color: getStrategyColorByPath([row.label]) || getColorOnIndex(theme.Colors, index),
            fundId: row.fundId,
          },
        ];
      }),
    },
  ];
}

/**
 * Hook to produce pie chart details for supported blocks.
 *
 * - PORTFOLIO_BREAKDOWN: builds hierarchy and series for strategies/funds.
 * - ASSET_EXPOSURE: delegates to asset exposure series builder.
 *
 * @param blockType
 * @param theme
 * @param data
 * @param metric
 * @param levels Selected levels (defaults to [0])
 * @param mergeSameLabelItems Optional
 * @param categoriesData Optional categories map for ASSET_EXPOSURE
 * @param hasMultiLevelPortfolioFiltering
 * @returns seriesEntries only for ASSET_EXPOSURE and colorMap/hierarchyTree/seriesEntries for PORTFOLIO_BREAKDOWN
 */
export function usePieChartDetails({
  blockType,
  theme,
  data,
  metric,
  levels = [],
  mergeSameLabelItems = false,
  categoriesData,
  hasMultiLevelPortfolioFiltering,
}: {
  blockType: CustomBlockTypeEnum;
  theme: Theme;
  data: CustomRow[];
  metric: Pick<CustomizableMetric, 'key' | 'label'> | undefined;
  levels?: (string | number)[];
  mergeSameLabelItems?: boolean;
  categoriesData?: { [_: string]: HoldingsCategoryInfo };
  hasMultiLevelPortfolioFiltering: boolean;
}): {
  colorMap?: ColorMap;
  hierarchyTree?: PortfolioBreakdownHierarchyNode[];
  seriesEntries?: VennSeriesEntry[];
} {
  const shouldUseRCMStrategyColors = useHasFF('rcm_strategy_colors_ff');

  switch (blockType) {
    case 'PORTFOLIO_BREAKDOWN':
      const portfolioBreakdownData = data as PortfolioRowData<PortfolioBreakdownRowData>[];

      if (hasMultiLevelPortfolioFiltering) {
        return getPortfolioBreakdownChartData(
          portfolioBreakdownData,
          theme,
          metric,
          levels,
          shouldUseRCMStrategyColors,
          mergeSameLabelItems,
        );
      }

      return {
        seriesEntries: shouldUseRCMStrategyColors
          ? privateGetCustomRCMPortfolioBreakdownRowData(portfolioBreakdownData, theme, metric)
          : privateGetPortfolioBreakdownRowData(portfolioBreakdownData, theme, metric),
      };

    case 'ASSET_EXPOSURE':
      // TODO: If only used by HoldingsPieChart.tsx, this case can be removed after confirmation.
      return { seriesEntries: getAssetExposurePieChartSeries(theme, metric?.label || '', data, categoriesData) };

    default:
      throw new Error('Unsupported Data Type');
  }
}
